import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Crown, Zap, Users } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { useDecisions } from "@/hooks/useDecisions";

interface SubscriptionStatusProps {
  onUpgrade: () => void;
}

export function SubscriptionStatus({ onUpgrade }: SubscriptionStatusProps) {
  const { subscription, loading, isPremium, getReviewDateLimit, getStatusMessage } = useSubscription();
  const { decisions } = useDecisions();

  if (loading || !subscription) {
    return null;
  }

  const currentDecisionCount = decisions.length;
  const isFreePlan = subscription.plan_type === 'free';

  const getPlanIcon = () => {
    switch (subscription.plan_type) {
      case 'annual':
        return <Zap className="h-4 w-4" />;
      case 'lifetime':
        return <Crown className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getPlanColor = () => {
    switch (subscription.plan_type) {
      case 'annual':
        return 'bg-blue-500';
      case 'lifetime':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {getPlanIcon()}
            <CardTitle className="text-lg">{getStatusMessage()}</CardTitle>
            <Badge 
              variant={subscription.status === 'active' ? 'default' : 'secondary'}
              className={subscription.status === 'active' ? getPlanColor() : ''}
            >
              {subscription.status}
            </Badge>
          </div>
          {isFreePlan && onUpgrade && (
            <Button onClick={onUpgrade} variant="outline" size="sm">
              Upgrade Plan
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Decisions tracked:</span>
            <span className="font-medium">{currentDecisionCount}</span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Review dates per decision:</span>
            <span className="font-medium">
              {isPremium() ? 'Unlimited' : getReviewDateLimit()}
            </span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Activity log:</span>
            <span className="font-medium">{isPremium() ? 'Included' : 'Premium only'}</span>
          </div>

          {!isPremium() && (
            <div className="mt-3 p-3 bg-muted border rounded-md">
              <p className="text-sm text-muted-foreground">
                Unlimited decisions on every plan. Upgrade for the activity log and
                unlimited review dates per decision.
                {onUpgrade && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ml-1 h-auto underline"
                    onClick={onUpgrade}
                  >
                    Upgrade
                  </Button>
                )}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}